/**
 * Dashboard customization page — create/manage dashboard views and arrange widgets on a
 * drag-and-drop grid.
 *
 * Header-only migration onto the CoOrdio shell chrome: `ShellHeader` + `PageContainer` replace the
 * hand-rolled MUI `Box` + `Typography` header so the outer chrome matches every migrated list
 * page. The drag-and-drop grid, view/widget forms, MUI `Grid` and `Card` panels are left as-is —
 * they own their own layout and swapping them for `@coordio/ui` primitives would need the widget
 * palette + `WidgetEditorDialog` to be redesigned together.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Chip, CoordioDropdown, Input, ShellHeader, Textarea } from '@coordio/ui';
import { InlineErrorPanel, PageContainer } from '@coordio/ui/layout';

import './DashboardCustomizationPage.scss';
import { Info, Pencil, Save, Trash2 } from 'lucide-react';
import { type Layout, type LayoutItem, useContainerWidth } from 'react-grid-layout';
import { ResponsiveReactGridLayout } from 'react-grid-layout/legacy';

import { dashboardService } from '../services/dashboardService';
import { useContextStore } from '../store/contextStore';
import type {
  CreateDashboardViewRequest,
  CreateDashboardWidgetRequest,
  DashboardWidgetDto,
  UpdateDashboardViewRequest,
  UpdateDashboardWidgetRequest,
  WidgetLayoutUpdate,
} from '../types/dashboard';
import { CostByModelChart } from '../components/dashboard/CostByModelChart';
import { MetricCardWidget } from '../components/dashboard/MetricCardWidget';
import { RealTimeMetrics } from '../components/dashboard/RealTimeMetrics';
import { UsageTrendsChart } from '../components/dashboard/UsageTrendsChart';
import { getWidgetSize, WidgetPalette, type WidgetTemplate } from '../components/dashboard/WidgetPalette';
import { WidgetEditorDialog } from '../components/dashboard/WidgetEditorDialog';
import { useConfirmDialog } from '../components/common/ConfirmDialog';

const COLS = 12;

const safeParseJson = (value: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

/**
 * Builds a non-overlapping grid layout. Undersized widgets (old saves) are expanded
 * to the type's minimum; items that all sit at 0,0 are packed left-to-right / top-down.
 */
const buildWidgetLayout = (widgets: DashboardWidgetDto[]): Layout => {
  const items = widgets.map((w) => {
    const size = getWidgetSize(w.widgetType);
    const storedW = Math.max(1, w.width ?? size.w);
    const storedH = Math.max(1, w.height ?? size.h);
    return {
      i: w.id,
      x: Math.max(0, w.positionX ?? 0),
      y: Math.max(0, w.positionY ?? 0),
      w: Math.min(COLS, Math.max(size.minW, storedW < size.minW ? size.w : storedW)),
      h: Math.max(size.minH, storedH < size.minH ? size.h : storedH),
      minW: size.minW,
      minH: size.minH,
    };
  });

  const allStacked = items.length > 1 && items.every((item) => item.x === items[0].x && item.y === items[0].y);
  if (!allStacked) return items;

  let cursorX = 0;
  let cursorY = 0;
  let rowH = 0;

  return items.map((item) => {
    if (cursorX + item.w > COLS) {
      cursorX = 0;
      cursorY += rowH;
      rowH = 0;
    }
    const placed = { ...item, x: cursorX, y: cursorY };
    cursorX += item.w;
    rowH = Math.max(rowH, item.h);
    return placed;
  });
};

/**
 * Dashboard customization page.
 */
export const DashboardCustomizationPage = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const { width, containerRef } = useContainerWidth();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const hasTenant = !!currentTenant?.id;

  const [selectedViewId, setSelectedViewId] = useState<string>('');
  const [createName, setCreateName] = useState('My Dashboard');
  const [createDescription, setCreateDescription] = useState('');
  const [createIsDefault, setCreateIsDefault] = useState(true);
  const [createIsShared, setCreateIsShared] = useState(false);

  const [layout, setLayout] = useState<Layout>([]);
  const [editingWidget, setEditingWidget] = useState<DashboardWidgetDto | null>(null);

  const reorderDebounceHandle = useRef<number | null>(null);
  const healedViewIds = useRef<Set<string>>(new Set());

  const viewsQuery = useQuery({
    queryKey: ['dashboardViews', currentTenant?.id],
    queryFn: () => dashboardService.getDashboardViews(),
    enabled: hasTenant,
  });

  const selectedViewQuery = useQuery({
    queryKey: ['dashboardView', currentTenant?.id, selectedViewId],
    enabled: hasTenant && !!selectedViewId,
    queryFn: () => dashboardService.getDashboardViewById(selectedViewId),
  });

  const selectedView = selectedViewQuery.data ?? null;

  // Initialize selection to first view (prefer default).
  useEffect(() => {
    const views = viewsQuery.data ?? [];
    if (views.length === 0) return;
    if (selectedViewId) return;

    const defaultView = views.find((v) => v.isDefault) ?? views[0];
    setSelectedViewId(defaultView.id);
  }, [selectedViewId, viewsQuery.data]);

  // Derive layout from widgets when a view is loaded/refreshed.
  useEffect(() => {
    if (!selectedView) return;
    const widgets = selectedView.widgets ?? [];
    const next = buildWidgetLayout(widgets);
    setLayout(next);
  }, [selectedView?.id, selectedView?.updatedAt]);

  const reorderWidgetsMutation = useMutation({
    mutationFn: async ({ dashboardViewId, updates }: { dashboardViewId: string; updates: WidgetLayoutUpdate[] }) =>
      dashboardService.reorderWidgets(dashboardViewId, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  // Persist healed sizes once per view so old tiny widgets stop collapsing after reload.
  useEffect(() => {
    if (!selectedView?.id || layout.length === 0) return;
    if (healedViewIds.current.has(selectedView.id)) return;

    const widgets = selectedView.widgets ?? [];
    const needsHeal = widgets.some((w) => {
      const size = getWidgetSize(w.widgetType);
      const storedW = w.width ?? 0;
      const storedH = w.height ?? 0;
      return storedW < size.minW || storedH < size.minH;
    });

    if (!needsHeal) {
      healedViewIds.current.add(selectedView.id);
      return;
    }

    healedViewIds.current.add(selectedView.id);
    const ordered = [...layout].sort((a, b) => a.y - b.y || a.x - b.x || a.i.localeCompare(b.i));
    const updates: WidgetLayoutUpdate[] = ordered.map((l, index) => ({
      widgetId: l.i,
      sortOrder: index,
      positionX: l.x,
      positionY: l.y,
      width: l.w,
      height: l.h,
    }));
    reorderWidgetsMutation.mutate({ dashboardViewId: selectedView.id, updates });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- heal once when layout first derived for a view
  }, [selectedView?.id, layout]);

  const createViewMutation = useMutation({
    mutationFn: async (req: CreateDashboardViewRequest) => dashboardService.createDashboardView(req),
    onSuccess: async (view) => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardViews'] });
      setSelectedViewId(view.id);
    },
  });

  const updateViewMutation = useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateDashboardViewRequest }) =>
      dashboardService.updateDashboardView(id, req),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardViews'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: async (id: string) => dashboardService.deleteDashboardView(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardViews'] });
      setSelectedViewId('');
    },
  });

  const createWidgetMutation = useMutation({
    mutationFn: async (req: CreateDashboardWidgetRequest) => dashboardService.createDashboardWidget(req),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const updateWidgetMutation = useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateDashboardWidgetRequest }) =>
      dashboardService.updateDashboardWidget(id, req),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const deleteWidgetMutation = useMutation({
    mutationFn: async (id: string) => dashboardService.deleteDashboardWidget(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const viewOptions = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);

  const renderWidgetBody = (w: DashboardWidgetDto) => {
    const config = safeParseJson(w.configurationJson ?? '{}');
    const rangeDays = typeof config.rangeDays === 'number' ? config.rangeDays : 30;
    const topN = typeof config.topN === 'number' ? config.topN : 6;
    const metric = typeof config.metric === 'string' ? config.metric : 'activeSessions';
    const chartType =
      config.chartType === 'line' || config.chartType === 'bar' || config.chartType === 'area'
        ? config.chartType
        : 'area';

    switch (w.widgetType) {
      case 'UsageTrends':
        return <UsageTrendsChart rangeDays={rangeDays} defaultChartType={chartType} />;
      case 'CostByModel':
        return <CostByModelChart rangeDays={rangeDays} topN={topN} />;
      case 'RealTimeMetrics':
        return <RealTimeMetrics embedded />;
      case 'MetricCard':
        return <MetricCardWidget metric={metric} />;
      default:
        return (
          <p className="text-sm text-[var(--color-text-secondary)]">
            Unknown widget type <strong>{w.widgetType}</strong>.
          </p>
        );
    }
  };

  const scheduleReorder = (dashboardViewId: string, nextLayout: Layout) => {
    if (reorderDebounceHandle.current) {
      window.clearTimeout(reorderDebounceHandle.current);
    }

    reorderDebounceHandle.current = window.setTimeout(() => {
      const ordered = [...nextLayout].sort((a: LayoutItem, b: LayoutItem) => a.y - b.y || a.x - b.x || a.i.localeCompare(b.i));
      const updates: WidgetLayoutUpdate[] = ordered.map((l, index) => ({
        widgetId: l.i,
        sortOrder: index,
        positionX: l.x,
        positionY: l.y,
        width: l.w,
        height: l.h,
      }));

      reorderWidgetsMutation.mutate({ dashboardViewId, updates });
    }, 400);
  };

  const handleAddWidget = (template: WidgetTemplate) => {
    if (!selectedView) return;

    const existing = selectedView.widgets ?? [];
    const nextSortOrder = existing.length === 0 ? 0 : Math.max(...existing.map((w) => w.sortOrder ?? 0)) + 1;
    const size = getWidgetSize(template.widgetType);

    // Place below the current layout so new widgets do not stack at 0,0.
    const nextY =
      layout.length === 0 ? 0 : Math.max(...layout.map((item) => item.y + item.h));

    const req: CreateDashboardWidgetRequest = {
      dashboardViewId: selectedView.id,
      widgetType: template.widgetType,
      title: template.title,
      description: template.description,
      configurationJson: template.configurationJson,
      positionX: 0,
      positionY: nextY,
      width: template.defaultWidth || size.w,
      height: template.defaultHeight || size.h,
      isVisible: true,
      sortOrder: nextSortOrder,
    };

    createWidgetMutation.mutate(req);
  };

  const handleSaveViewLayoutJson = async () => {
    if (!selectedView) return;

    const req: UpdateDashboardViewRequest = {
      name: selectedView.name,
      description: selectedView.description,
      isDefault: selectedView.isDefault,
      isShared: selectedView.isShared,
      layoutJson: JSON.stringify(layout),
    };

    await updateViewMutation.mutateAsync({ id: selectedView.id, req });
  };

  const handleUpdateWidget = async (widgetId: string, req: UpdateDashboardWidgetRequest) => {
    await updateWidgetMutation.mutateAsync({ id: widgetId, req });
  };

  const isBusy =
    viewsQuery.isLoading ||
    selectedViewQuery.isLoading ||
    createViewMutation.isPending ||
    updateViewMutation.isPending ||
    deleteViewMutation.isPending ||
    createWidgetMutation.isPending ||
    updateWidgetMutation.isPending ||
    deleteWidgetMutation.isPending ||
    reorderWidgetsMutation.isPending;

  return (
    <div className="dashboard-customization" data-testid="dashboard-customize-detail-page">
      <ShellHeader
        title="Dashboard Customization"
        description="Create dashboard views and arrange widgets with drag-and-drop."
      />

      <PageContainer>
        {(viewsQuery.isError || selectedViewQuery.isError) && (
          <div className="dashboard-customization__error">
            <InlineErrorPanel
              title="Unable to load dashboard views"
              message="Ensure the backend API is running and you have selected a tenant/project."
              data-testid="dashboard-customize-detail-error"
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_2fr]">
        {/* Left column: view management + palette */}
        <div>
          <div className="mb-3 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
            <h6 className="mb-2 text-base font-semibold">Views</h6>

            <div>
              <label className="mb-1 block text-sm font-medium">Selected view</label>
              <CoordioDropdown
                options={viewOptions.map((v) => ({
                  value: v.id,
                  label: `${v.name} ${v.isDefault ? '(Default)' : ''}`.trim(),
                }))}
                value={selectedViewId || null}
                onChange={(val) => setSelectedViewId(val ?? '')}
                size="sm"
                clearable={false}
                disabled={viewOptions.length === 0}
              />
            </div>

              {selectedView && (
                <div className="mt-2 flex flex-wrap gap-1">
                  <Chip label={`Widgets: ${selectedView.widgetCount ?? (selectedView.widgets?.length ?? 0)}`} />
                  <Chip variant="outlined" label={`Updated: ${formatDateTime(selectedView.updatedAt)}`} />
                </div>
              )}

              <hr className="my-3 border-[var(--color-border-default)]" />

              <h6 className="mb-2 text-base font-semibold">Create new view</h6>
              <div className="grid gap-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">Name</label>
                  <Input value={createName} onChange={(e) => setCreateName(e.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Description</label>
                  <Textarea
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Default</label>
                  <CoordioDropdown
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    value={createIsDefault ? 'yes' : 'no'}
                    onChange={(val) => setCreateIsDefault(val === 'yes')}
                    size="sm"
                    clearable={false}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Shared</label>
                  <CoordioDropdown
                    options={[
                      { value: 'no', label: 'No' },
                      { value: 'yes', label: 'Yes' },
                    ]}
                    value={createIsShared ? 'yes' : 'no'}
                    onChange={(val) => setCreateIsShared(val === 'yes')}
                    size="sm"
                    clearable={false}
                  />
                </div>

                <Button
                  type="button"
                  variant="default"
                  disabled={!createName.trim() || isBusy}
                  onClick={() =>
                    createViewMutation.mutate({
                      name: createName.trim(),
                      description: createDescription.trim() || undefined,
                      isDefault: createIsDefault,
                      isShared: createIsShared,
                      layoutJson: '{}',
                    })
                  }
                  data-testid="create-dashboard-customize-btn"
                >
                  Create View
                </Button>
              </div>

              {selectedView && (
                <div className="mt-3 flex gap-1">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="gap-1"
                    disabled={isBusy}
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Delete dashboard view',
                        message: `Delete dashboard view "${selectedView.name}"? This cannot be undone.`,
                        confirmText: 'Delete',
                      });
                      if (!ok) return;
                      deleteViewMutation.mutate(selectedView.id);
                    }}
                    data-testid="delete-dashboard-customize-btn"
                  >
                    <Trash2 className="h-4 w-4" /> Delete View
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={isBusy}
                    onClick={handleSaveViewLayoutJson}
                    data-testid="save-dashboard-customize-btn"
                  >
                    <Save className="h-4 w-4" /> Save Layout JSON
                  </Button>
                </div>
              )}
          </div>

          <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
            <WidgetPalette onAdd={handleAddWidget} />
          </div>
        </div>

        {/* Right column: layout canvas */}
        <div>
          <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
            <h6 className="mb-1 text-base font-semibold">Canvas</h6>
            <p className="mb-2 text-sm text-[var(--color-text-secondary)]">
              Drag widgets to reposition, drag handles to resize. Changes are auto-saved (debounced) to the backend reorder endpoint.
            </p>

              {!selectedView && !viewsQuery.isLoading && viewOptions.length === 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-200">
                  <Info size={18} className="flex-shrink-0 text-blue-500" />
                  No views yet for this workspace. Create a view on the left, then add Usage Trends / Cost by Model widgets to see live charts.
                </div>
              )}

              {!selectedView && (viewsQuery.isLoading || viewOptions.length > 0) && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-200">
                  <Info size={18} className="flex-shrink-0 text-blue-500" />
                  {viewsQuery.isLoading
                    ? 'Loading dashboard views…'
                    : 'Create a view (or select an existing one) to start customizing.'}
                </div>
              )}

              {selectedView && (
                <div ref={containerRef} className="dashboard-customization__canvas">
                  {width > 0 ? (
                    <ResponsiveReactGridLayout
                      width={width}
                      className="layout"
                      layouts={{ lg: layout }}
                      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                      cols={{ lg: COLS, md: COLS, sm: 6, xs: 4, xxs: 2 }}
                      rowHeight={36}
                      margin={[12, 12]}
                      containerPadding={[8, 8]}
                      compactType="vertical"
                      preventCollision={false}
                      isResizable
                      isDraggable
                      draggableCancel="button, a, input, textarea, select"
                      onLayoutChange={(next: Layout) => {
                        const widgets = selectedView.widgets ?? [];
                        const withMins = next.map((item) => {
                          const widget = widgets.find((w) => w.id === item.i);
                          const size = getWidgetSize(widget?.widgetType ?? '');
                          return {
                            ...item,
                            minW: size.minW,
                            minH: size.minH,
                            w: Math.max(item.w, size.minW),
                            h: Math.max(item.h, size.minH),
                          };
                        });
                        setLayout(withMins);
                        scheduleReorder(selectedView.id, withMins);
                      }}
                    >
                      {(selectedView.widgets ?? []).map((w) => (
                        <div key={w.id}>
                          <div className="dashboard-customization__widget">
                            <div className="dashboard-customization__widget-header">
                              <div className="dashboard-customization__widget-title">
                                <p title={w.title}>{w.title}</p>
                                <span>{w.widgetType}</span>
                              </div>
                              <div className="dashboard-customization__widget-actions">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="gap-1"
                                  onClick={() => setEditingWidget(w)}
                                >
                                  <Pencil className="h-3.5 w-3.5" /> Edit
                                </Button>
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="gap-1"
                                  onClick={async () => {
                                    const ok = await confirm({
                                      title: 'Delete widget',
                                      message: `Delete widget "${w.title}"?`,
                                      confirmText: 'Delete',
                                    });
                                    if (!ok) return;
                                    deleteWidgetMutation.mutate(w.id);
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Delete
                                </Button>
                              </div>
                            </div>

                            <div className="dashboard-customization__widget-body">{renderWidgetBody(w)}</div>
                          </div>
                        </div>
                      ))}
                    </ResponsiveReactGridLayout>
                  ) : (
                    <p className="p-4 text-sm text-[var(--color-text-secondary)]">Measuring canvas…</p>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

        <WidgetEditorDialog open={!!editingWidget} widget={editingWidget} onClose={() => setEditingWidget(null)} onSave={handleUpdateWidget} />
      </PageContainer>
    </div>
  );
};

